import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';

export interface PointTransaction {
  id: string;
  user_id: string;
  amount: number;
  transaction_type: 'earned' | 'spent' | 'admin_adjustment';
  source_type: 'poll_vote' | 'survey_completion' | 'admin_adjustment';
  source_id?: string;
  description: string;
  country_code: string;
  admin_id?: string;
  created_at: string;
}

export interface UserPoints {
  id: string;
  user_id: string;
  country_code: string;
  total_points: number;
  lifetime_earned: number;
  last_redemption_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CountryPointsConfig {
  id: string;
  country_code: string;
  points_enabled: boolean;
  poll_reward: number;
  monthly_limit: number;
  min_redemption_amount: number;
  currency_symbol: string;
  created_at: string;
  updated_at: string;
}

export interface UserCountryVerification {
  id: string;
  user_id: string;
  country_code: string;
  constituency_code?: string;
  verification_status: 'pending' | 'verified' | 'rejected' | 'updated';
  primary_verification: boolean;
  verified_at?: string;
  created_at: string;
  updated_at: string;
}

export class PointsService {
  /**
   * Get poll's country code from database
   */
  static async getPollCountryCode(pollId: string): Promise<string | null> {
    try {
      console.log('POINTS_SERVICE: Fetching country code for poll:', pollId);
      
      const { data: poll, error } = await supabase
        .from('polls')
        .select('country_code')
        .eq('id', pollId)
        .single();

      if (error) {
        console.error('POINTS_SERVICE: Error fetching poll country code:', error);
        return null;
      }

      console.log('POINTS_SERVICE: Poll country code result:', poll?.country_code);
      return poll?.country_code || null;
    } catch (error) {
      console.error('POINTS_SERVICE: Exception fetching poll country code:', error);
      return null;
    }
  }

  /**
   * Award points to a user for poll voting with FIXED data type handling
   */
  static async awardPollPoints(
    userId: string,
    pollId: string,
    countryCode: string
  ): Promise<{ success: boolean; points?: number; error?: string }> {
    try {
      console.log('POINTS_SERVICE: ===== STARTING AWARD POLL POINTS =====');
      console.log('POINTS_SERVICE: Starting awardPollPoints with params:', {
        userId,
        pollId,
        countryCode,
        pollIdType: typeof pollId
      });

      // Check if user is verified for this country
      console.log('POINTS_SERVICE: Checking user verification status...');
      const isVerified = await this.isUserVerifiedForCountry(userId, countryCode);
      console.log('POINTS_SERVICE: User verification check result:', {
        userId,
        countryCode,
        isVerified
      });
      
      if (!isVerified) {
        console.log('POINTS_SERVICE: User not verified for country, cannot award points');
        return { success: false, error: 'User not verified for this country' };
      }

      // Check if points are enabled for this country
      console.log('POINTS_SERVICE: Checking country points configuration...');
      const config = await this.getCountryConfig(countryCode);
      console.log('POINTS_SERVICE: Country config check result:', {
        countryCode,
        config: config ? {
          points_enabled: config.points_enabled,
          poll_reward: config.poll_reward,
          monthly_limit: config.monthly_limit
        } : null
      });
      
      if (!config || !config.points_enabled) {
        console.log('POINTS_SERVICE: Points not enabled for country');
        return { success: true, points: 0 }; // No error, just no points awarded
      }

      // FIXED: Check if user has already been awarded points for this poll with explicit UUID casting
      console.log('POINTS_SERVICE: Checking for existing point transaction with explicit UUID casting...');
      console.log('POINTS_SERVICE: Query parameters:', {
        userId,
        pollId,
        pollIdAsString: String(pollId),
        sourceType: 'poll_vote'
      });

      const { data: existingTransaction, error: transactionCheckError } = await supabase
        .from('point_transactions')
        .select('id, source_id, user_id')
        .eq('user_id', userId)
        .eq('source_id', pollId) // pollId is already a string, but ensure explicit comparison
        .eq('source_type', 'poll_vote')
        .maybeSingle(); // Use maybeSingle to avoid 406 error

      if (transactionCheckError) {
        console.error('POINTS_SERVICE: Error checking existing transactions:', {
          error: transactionCheckError,
          message: transactionCheckError.message,
          details: transactionCheckError.details,
          hint: transactionCheckError.hint,
          code: transactionCheckError.code
        });
        // Don't throw here, continue with awarding points as this might be a data type issue
        console.warn('POINTS_SERVICE: Continuing despite transaction check error - might be existing data type mismatch');
      }

      if (existingTransaction) {
        console.log('POINTS_SERVICE: Points already awarded for this poll:', {
          transactionId: existingTransaction.id,
          existingSourceId: existingTransaction.source_id,
          existingUserId: existingTransaction.user_id,
          queryPollId: pollId
        });
        return { success: true, points: 0 }; // Already awarded
      }

      console.log('POINTS_SERVICE: No existing transaction found, proceeding with award');

      // FIXED: Check monthly limit with proper date range calculation
      console.log('POINTS_SERVICE: Checking monthly limit...');
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      
      console.log('POINTS_SERVICE: Date range for monthly calculation:', {
        startOfMonth: startOfMonth.toISOString(),
        endOfMonth: endOfMonth.toISOString()
      });

      const { data: monthlyTransactions, error: monthlyError } = await supabase
        .from('point_transactions')
        .select('amount')
        .eq('user_id', userId)
        .eq('country_code', countryCode)
        .eq('transaction_type', 'earned')
        .gte('created_at', startOfMonth.toISOString())
        .lt('created_at', endOfMonth.toISOString());

      if (monthlyError) {
        console.error('POINTS_SERVICE: Error checking monthly total:', monthlyError);
        throw monthlyError;
      }

      const currentMonthlyTotal = monthlyTransactions?.reduce((sum, t) => sum + Number(t.amount), 0) || 0;
      console.log('POINTS_SERVICE: Monthly limit check:', {
        currentMonthlyTotal,
        pollReward: config.poll_reward,
        monthlyLimit: config.monthly_limit,
        wouldExceed: currentMonthlyTotal + config.poll_reward > config.monthly_limit
      });
      
      if (currentMonthlyTotal + config.poll_reward > config.monthly_limit) {
        console.log('POINTS_SERVICE: Monthly limit would be exceeded');
        return { 
          success: false, 
          error: `Monthly limit of ${config.currency_symbol}${config.monthly_limit} reached` 
        };
      }

      // FIXED: Award points with proper source_id handling (pollId as string)
      console.log('POINTS_SERVICE: All checks passed, inserting point transaction...');
      console.log('POINTS_SERVICE: Transaction data to insert:', {
        user_id: userId,
        amount: config.poll_reward,
        transaction_type: 'earned',
        source_type: 'poll_vote',
        source_id: pollId,
        description: 'Poll vote reward',
        country_code: countryCode
      });

      const { data: transactionData, error: transactionError } = await supabase
        .from('point_transactions')
        .insert({
          user_id: userId,
          amount: config.poll_reward,
          transaction_type: 'earned',
          source_type: 'poll_vote',
          source_id: pollId, // Keep as string - this matches the column type
          description: `Poll vote reward`,
          country_code: countryCode
        })
        .select()
        .single();

      if (transactionError) {
        console.error('POINTS_SERVICE: Error inserting transaction:', {
          error: transactionError,
          message: transactionError.message,
          details: transactionError.details,
          hint: transactionError.hint,
          code: transactionError.code
        });
        throw transactionError;
      }

      console.log('POINTS_SERVICE: Transaction inserted successfully:', transactionData);

      // Update user points with FIXED upsert handling
      console.log('POINTS_SERVICE: Updating user points total...');
      try {
        const updateResult = await this.updateUserPointsTotal(userId, countryCode);
        if (!updateResult.success) {
          console.error('POINTS_SERVICE: Error updating user points total:', updateResult.error);
          // Don't fail the whole operation, but log it
          console.warn('POINTS_SERVICE: Continuing despite points total update failure');
        } else {
          console.log('POINTS_SERVICE: User points total updated successfully');
        }
      } catch (updateError) {
        console.error('POINTS_SERVICE: Exception updating user points total:', updateError);
        // Don't fail the whole operation if this fails, but log it
        console.warn('POINTS_SERVICE: Continuing despite points total update failure');
      }

      console.log('POINTS_SERVICE: Successfully awarded points:', config.poll_reward);
      console.log('POINTS_SERVICE: ===== AWARD POLL POINTS COMPLETED =====');
      return { success: true, points: config.poll_reward };
    } catch (error) {
      console.error('POINTS_SERVICE: CRITICAL ERROR in awardPollPoints:', error);
      console.error('POINTS_SERVICE: Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace',
        type: typeof error
      });
      return { success: false, error: 'Failed to award points due to system error' };
    }
  }

  /**
   * Check if user is verified for a specific country with improved error handling
   */
  static async isUserVerifiedForCountry(userId: string, countryCode: string): Promise<boolean> {
    try {
      console.log('POINTS_SERVICE: Checking user verification for country:', { userId, countryCode });
      
      const { data, error } = await supabase
        .from('user_country_verifications')
        .select('id')
        .eq('user_id', userId)
        .eq('country_code', countryCode)
        .eq('verification_status', 'verified')
        .maybeSingle(); // Use maybeSingle to avoid 406 error

      if (error) {
        console.error('POINTS_SERVICE: Error checking user verification:', error);
        return false; // Fail safe - don't award points if verification check fails
      }
      
      const isVerified = !!data;
      console.log('POINTS_SERVICE: User verification result:', { isVerified, hasData: !!data });
      return isVerified;
    } catch (error) {
      console.error('POINTS_SERVICE: Exception checking user country verification:', error);
      return false; // Fail safe
    }
  }

  /**
   * Get all verified countries for a user
   */
  static async getUserVerifiedCountries(userId: string): Promise<UserCountryVerification[]> {
    try {
      const { data, error } = await supabase
        .from('user_country_verifications')
        .select('*')
        .eq('user_id', userId)
        .eq('verification_status', 'verified')
        .order('primary_verification', { ascending: false })
        .order('verified_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user verified countries:', error);
      return [];
    }
  }

  /**
   * Get user's primary verified country
   */
  static async getUserPrimaryCountry(userId: string): Promise<UserCountryVerification | null> {
    try {
      const { data, error } = await supabase
        .from('user_country_verifications')
        .select('*')
        .eq('user_id', userId)
        .eq('verification_status', 'verified')
        .eq('primary_verification', true)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching user primary country:', error);
      return null;
    }
  }

  /**
   * Set primary country for a user
   */
  static async setPrimaryCountry(userId: string, countryCode: string): Promise<{ success: boolean; error?: string }> {
    try {
      // First, remove primary status from all countries
      await supabase
        .from('user_country_verifications')
        .update({ primary_verification: false })
        .eq('user_id', userId);

      // Then set the new primary country
      const { error } = await supabase
        .from('user_country_verifications')
        .update({ primary_verification: true })
        .eq('user_id', userId)
        .eq('country_code', countryCode)
        .eq('verification_status', 'verified');

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error setting primary country:', error);
      return { success: false, error: 'Failed to set primary country' };
    }
  }

  /**
   * Create user points record when user gets verified for a country
   */
  static async createUserPointsForCountry(userId: string, countryCode: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Check if user points record already exists
      const existingPoints = await this.getUserPoints(userId, countryCode);
      if (existingPoints) {
        return { success: true }; // Already exists
      }

      // Create new user points record
      const { error } = await supabase
        .from('user_points')
        .insert({
          user_id: userId,
          country_code: countryCode,
          total_points: 0,
          lifetime_earned: 0
        });

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error creating user points for country:', error);
      return { success: false, error: 'Failed to create user points record' };
    }
  }

  /**
   * Get all user points for all verified countries
   */
  static async getAllUserPoints(userId: string): Promise<UserPoints[]> {
    try {
      const { data, error } = await supabase
        .from('user_points')
        .select('*')
        .eq('user_id', userId)
        .order('total_points', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching all user points:', error);
      return [];
    }
  }

  /**
   * Admin adjustment of user points
   */
  static async adminAdjustPoints(
    userId: string,
    amount: number,
    description: string,
    countryCode: string,
    adminId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const transactionType = amount > 0 ? 'earned' : 'spent';
      
      const { error } = await supabase
        .from('point_transactions')
        .insert({
          user_id: userId,
          amount: Math.abs(amount),
          transaction_type: transactionType,
          source_type: 'admin_adjustment',
          description,
          country_code: countryCode,
          admin_id: adminId
        });

      if (error) throw error;

      // Update user points total
      await this.updateUserPointsTotal(userId, countryCode);

      return { success: true };
    } catch (error) {
      console.error('Error adjusting points:', error);
      return { success: false, error: 'Failed to adjust points' };
    }
  }

  /**
   * Get user's current points for a country
   */
  static async getUserPoints(userId: string, countryCode: string): Promise<UserPoints | null> {
    try {
      const { data, error } = await supabase
        .from('user_points')
        .select('*')
        .eq('user_id', userId)
        .eq('country_code', countryCode)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching user points:', error);
      return null;
    }
  }

  /**
   * Get user's transaction history
   */
  static async getUserTransactions(
    userId: string, 
    countryCode: string,
    limit = 50,
    offset = 0
  ): Promise<PointTransaction[]> {
    try {
      const { data, error } = await supabase
        .from('point_transactions')
        .select('*')
        .eq('user_id', userId)
        .eq('country_code', countryCode)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;
      
      return (data || []).map(transaction => ({
        id: transaction.id,
        user_id: transaction.user_id,
        amount: Number(transaction.amount),
        transaction_type: transaction.transaction_type as 'earned' | 'spent' | 'admin_adjustment',
        source_type: transaction.source_type as 'poll_vote' | 'survey_completion' | 'admin_adjustment',
        source_id: transaction.source_id,
        description: transaction.description,
        country_code: transaction.country_code,
        admin_id: transaction.admin_id,
        created_at: transaction.created_at
      }));
    } catch (error) {
      console.error('Error fetching user transactions:', error);
      return [];
    }
  }

  /**
   * Get country points configuration
   */
  static async getCountryConfig(countryCode: string): Promise<CountryPointsConfig | null> {
    try {
      console.log('POINTS_SERVICE: Fetching country config for:', countryCode);
      
      const { data, error } = await supabase
        .from('country_points_config')
        .select('*')
        .eq('country_code', countryCode)
        .maybeSingle(); // Use maybeSingle to avoid 406 error

      if (error) {
        console.error('POINTS_SERVICE: Error fetching country config:', error);
        throw error;
      }
      
      console.log('POINTS_SERVICE: Country config result:', data);
      return data;
    } catch (error) {
      console.error('POINTS_SERVICE: Error fetching country config:', error);
      return null;
    }
  }

  /**
   * Update country points configuration (admin only)
   */
  static async updateCountryConfig(
    countryCode: string,
    config: Partial<CountryPointsConfig>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Use upsert to handle both insert and update cases
      const { error } = await supabase
        .from('country_points_config')
        .upsert({
          country_code: countryCode,
          ...config,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'country_code',
          ignoreDuplicates: false
        });

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error updating country config:', error);
      return { success: false, error: 'Failed to update configuration' };
    }
  }

  /**
   * Update user's total points (internal method) with FIXED upsert handling
   */
  private static async updateUserPointsTotal(userId: string, countryCode: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('POINTS_SERVICE: Starting updateUserPointsTotal for:', { userId, countryCode });

      // Calculate totals from transactions
      const { data: transactions, error: transactionError } = await supabase
        .from('point_transactions')
        .select('amount, transaction_type')
        .eq('user_id', userId)
        .eq('country_code', countryCode);

      if (transactionError) {
        console.error('POINTS_SERVICE: Error fetching transactions for total calculation:', transactionError);
        throw transactionError;
      }

      if (!transactions) {
        console.log('POINTS_SERVICE: No transactions found for user');
        return { success: true };
      }

      const earned = transactions
        .filter(t => t.transaction_type === 'earned')
        .reduce((sum, t) => sum + Number(t.amount), 0);
      
      const spent = transactions
        .filter(t => t.transaction_type === 'spent')
        .reduce((sum, t) => sum + Number(t.amount), 0);

      const totalPoints = earned - spent;

      console.log('POINTS_SERVICE: Calculated totals:', {
        earned,
        spent,
        totalPoints,
        transactionCount: transactions.length
      });

      // FIXED: Use proper upsert with onConflict to handle unique constraint
      const { data: upsertResult, error: upsertError } = await supabase
        .from('user_points')
        .upsert({
          user_id: userId,
          country_code: countryCode,
          total_points: totalPoints,
          lifetime_earned: earned,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,country_code',
          ignoreDuplicates: false
        })
        .select();

      if (upsertError) {
        console.error('POINTS_SERVICE: Error during upsert:', upsertError);
        throw upsertError;
      }

      console.log('POINTS_SERVICE: Successfully updated user points:', upsertResult);
      return { success: true };
    } catch (error) {
      console.error('POINTS_SERVICE: Error updating user points total:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error updating user points total' 
      };
    }
  }

  /**
   * Check if poll has points enabled with improved validation
   */
  static async isPollPointsEnabled(pollId: string): Promise<boolean> {
    try {
      console.log('POINTS_SERVICE: Checking if poll has points enabled:', pollId);
      
      const { data: poll, error } = await supabase
        .from('polls')
        .select('points_enabled')
        .eq('id', pollId)
        .maybeSingle(); // Use maybeSingle to avoid 406 error

      if (error) {
        console.error('POINTS_SERVICE: Error checking poll points status:', error);
        return false;
      }

      const isEnabled = poll?.points_enabled === true;
      console.log('POINTS_SERVICE: Poll points enabled result:', { pollId, isEnabled, rawValue: poll?.points_enabled });
      return isEnabled;
    } catch (error) {
      console.error('POINTS_SERVICE: Exception checking poll points status:', error);
      return false;
    }
  }

  /**
   * Toggle points for a specific poll (admin only)
   */
  static async togglePollPoints(pollId: string, enabled: boolean): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('polls')
        .update({ points_enabled: enabled })
        .eq('id', pollId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error toggling poll points:', error);
      return { success: false, error: 'Failed to update poll points setting' };
    }
  }
}
